import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UserService } from '../../core/services/user.service';
import { ToastService } from '../../core/services/toast.service';
import { AuthService } from '../../core/services/auth.service';
import { UserProfileResponse, CreateUserRequest, UpdateUserRequest, UserRank } from '../../core/models/models';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { ModalComponent, ModalErrorState } from '../../shared/components/modal/modal.component';
import { extractErrorDetails } from '../../core/interceptors/response.interceptor';
import { PhoneInputComponent } from '../../shared/components/phone-input/phone-input.component';

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [CommonModule, FormsModule, StatusBadgeComponent, ModalComponent, PhoneInputComponent],
  template: `
    <div class="space-y-6 font-sans">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-extrabold text-slate-900 tracking-tight">
            @if (authService.hasRole('SuperAdmin') || authService.isInternal()) {
              Global User Directory & Role Assignment Matrix
            } @else {
              {{ authService.institutionName() }} User Directory
            }
          </h1>
          <p class="text-xs text-slate-500">
            @if (authService.hasRole('SuperAdmin') || authService.isInternal()) {
              Manage portal access, institutional ranks, and assign granular security roles across all network nodes.
            } @else {
              Manage portal officers and role assignments strictly for {{ authService.institutionName() }}.
            }
          </p>
        </div>

        @if (authService.hasPermission('can assign role') || authService.isInstitutionAdmin() || authService.hasRole('SuperAdmin')) {
          <button
            (click)="openCreateModal()"
            class="inline-flex items-center gap-2 py-2.5 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-md shadow-emerald-600/20 transition-all active:scale-95 cursor-pointer"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Create New Officer Account
          </button>
        }
      </div>

      <!-- Scope Information Banner for Institution Admins -->
      @if (!authService.hasRole('SuperAdmin') && !authService.isInternal()) {
        <div class="p-3.5 rounded-2xl bg-emerald-50/70 border border-emerald-200/80 flex items-center justify-between gap-3 text-xs text-emerald-900">
          <div class="flex items-center gap-2.5">
            <span class="p-1.5 rounded-xl bg-emerald-200/60 text-emerald-800">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </span>
            <span>
              <strong>Institution-Scoped Management:</strong> You are managing user accounts and operational roles strictly for <strong>{{ authService.institutionName() }}</strong>. SuperAdmin privileges are reserved for eGAZ national regulators.
            </span>
          </div>
          <span class="px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-300 shrink-0">
            Institutional Scope Active
          </span>
        </div>
      }

      <!-- Search & Filters -->
      <div class="glass-panel p-4 rounded-2xl border border-slate-200 bg-white flex items-center justify-between gap-4 shadow-xs">
        <input
          type="text"
          [(ngModel)]="searchQuery"
          placeholder="Search by officer name, email, or department..."
          class="px-4 py-2 rounded-xl bg-white border border-slate-300 text-xs text-slate-900 placeholder-slate-400 w-72 focus:border-emerald-500 focus:outline-none"
        />

        <div class="text-xs text-slate-500 font-mono font-semibold">
          Total Registered Officers: <span class="text-emerald-700 font-bold">{{ filteredUsers().length }}</span>
        </div>
      </div>

      <!-- Users Data Table -->
      <div class="glass-panel rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-xs">
        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
                <th class="p-4">User Officer</th>
                <th class="p-4">Official Email</th>
                <th class="p-4">Institution</th>
                <th class="p-4">Rank</th>
                <th class="p-4">Assigned Roles</th>
                <th class="p-4">Status</th>
                <th class="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-200/80">
              @for (usr of filteredUsers(); track usr.uid) {
                <tr class="hover:bg-slate-50/70 transition-colors">
                  <td class="p-4">
                    <div class="font-bold text-slate-900">{{ usr.firstName }} {{ usr.middleName || '' }} {{ usr.lastName }}</div>
                    <div class="text-[11px] text-slate-500 font-mono">{{ usr.phone }}</div>
                  </td>
                  <td class="p-4 font-mono text-emerald-700 font-bold">{{ usr.email }}</td>
                  <td class="p-4 text-slate-700 font-medium">
                    {{ usr.institutionName || 'Zanzibar Central Node' }}
                  </td>
                  <td class="p-4 font-mono text-[11px] text-slate-600 font-semibold">
                    <span
                      class="px-2 py-0.5 rounded-full text-[10px] font-bold"
                      [ngClass]="usr.rank === UserRank.INTERNAL ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-slate-100 text-slate-700 border border-slate-200'"
                    >
                      {{ usr.rank }}
                    </span>
                  </td>
                  <td class="p-4">
                    <div class="flex flex-wrap gap-1">
                      @for (r of usr.roles; track r) {
                        <span
                          class="px-2 py-0.5 rounded text-[10px] font-bold"
                          [ngClass]="r === 'SuperAdmin' ? 'bg-purple-100 text-purple-900 border border-purple-300 font-extrabold' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'"
                        >
                          {{ r }}
                        </span>
                      }
                    </div>
                  </td>
                  <td class="p-4">
                    <app-status-badge [status]="usr.status"></app-status-badge>
                  </td>
                  <td class="p-4 text-right">
                    <div class="flex items-center justify-end gap-1.5">
                      @if (canEditUser(usr)) {
                        <button
                          (click)="openEditModal(usr)"
                          title="Edit Officer Details"
                          class="p-1.5 rounded-lg bg-slate-100 hover:bg-emerald-50 text-slate-600 hover:text-emerald-700 border border-slate-200 hover:border-emerald-300 transition-colors cursor-pointer"
                        >
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      }

                      @if (canAssignRoles(usr)) {
                        <button
                          (click)="openAssignRolesModal(usr)"
                          title="Assign Security Roles"
                          class="px-2.5 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-600 hover:text-white font-bold text-emerald-700 text-xs transition-colors border border-emerald-200 shadow-2xs cursor-pointer flex items-center gap-1"
                        >
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                          <span>Roles</span>
                        </button>
                      }

                      @if (canDeleteUser(usr)) {
                        <button
                          (click)="openDeleteModal(usr)"
                          title="Delete Officer Account"
                          class="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-600 hover:text-white text-rose-600 border border-rose-200 transition-colors cursor-pointer"
                        >
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      }

                      @if (!canEditUser(usr) && !canAssignRoles(usr) && !canDeleteUser(usr)) {
                        <span class="text-[11px] text-slate-400 italic font-medium">Restricted</span>
                      }
                    </div>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td colspan="7" class="p-8 text-center text-slate-500">
                    No users found matching your search criteria.
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </div>

      <!-- Create User Modal -->
      <app-modal
        [isOpen]="showCreateModal()"
        title="Register Portal Officer Account"
        [errorState]="modalError()"
        (close)="showCreateModal.set(false)"
      >
        <form (ngSubmit)="onCreateUser()" class="space-y-4">
          <!-- Institutional Scope Notice -->
          @if (!authService.hasRole('SuperAdmin') && !authService.isInternal()) {
            <div class="p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600">
              Creating officer under: <strong class="text-slate-900">{{ authService.institutionName() }}</strong>
            </div>
          }

          <div class="grid grid-cols-3 gap-3">
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">First Name <span class="text-rose-500">*</span></label>
              <input
                type="text"
                [(ngModel)]="newUser.firstName"
                name="firstName"
                required
                placeholder="First Name"
                class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:outline-none"
              />
            </div>
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Middle Name</label>
              <input
                type="text"
                [(ngModel)]="newUser.middleName"
                name="middleName"
                placeholder="Middle"
                class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:outline-none"
              />
            </div>
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Last Name <span class="text-rose-500">*</span></label>
              <input
                type="text"
                [(ngModel)]="newUser.lastName"
                name="lastName"
                required
                placeholder="Last Name"
                class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:outline-none"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Official Email <span class="text-rose-500">*</span></label>
              <input
                type="email"
                [(ngModel)]="newUser.email"
                name="email"
                required
                placeholder="officer@agency.go.tz"
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:outline-none"
              />
            </div>

            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Phone Number <span class="text-rose-500">*</span></label>
              <app-phone-input
                [(ngModel)]="newUser.phone"
                name="phone"
                [required]="true"
                placeholder="777 000 000"
              ></app-phone-input>
            </div>
          </div>

          <!-- Password field -->
          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Initial Password <span class="text-rose-500">*</span></label>
            <input
              type="password"
              [(ngModel)]="newUser.password"
              name="password"
              required
              minlength="8"
              placeholder="Minimum 8 characters"
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:outline-none"
            />
            <p class="text-[10px] text-slate-400 mt-1">A temporary password notification will also be dispatched via email.</p>
          </div>

          <div>
            <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Institutional Rank Scope <span class="text-rose-500">*</span></label>
            <select
              [(ngModel)]="newUser.rank"
              name="rank"
              required
              class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:outline-none"
            >
              <option [ngValue]="UserRank.INSTITUTION_USER">INSTITUTION_USER (Standard Officer)</option>
              @if (authService.hasRole('SuperAdmin') || authService.isInternal()) {
                <option [ngValue]="UserRank.INSTITUTION_ADMIN">INSTITUTION_ADMIN (Institution Manager)</option>
                <option [ngValue]="UserRank.INTERNAL">INTERNAL (eGAZ Regulator Admin)</option>
              }
            </select>
          </div>

          <div class="flex justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              (click)="showCreateModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-sm cursor-pointer"
            >
              Create Officer Account
            </button>
          </div>
        </form>
      </app-modal>

      <!-- Assign Roles Modal Matrix -->
      <app-modal
        [isOpen]="showRolesModal()"
        [title]="'Assign Security Roles: ' + (selectedUser()?.firstName || '') + ' ' + (selectedUser()?.lastName || '')"
        (close)="showRolesModal.set(false)"
      >
        <div class="space-y-4">
          <p class="text-xs text-slate-600">
            Select security roles to grant capability permissions to this user officer across the network.
          </p>

          <div class="space-y-2">
            @for (roleName of availableRoles(); track roleName) {
              <label class="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200 hover:border-emerald-300 cursor-pointer transition">
                <input
                  type="checkbox"
                  [checked]="isRoleSelected(roleName)"
                  (change)="toggleRole(roleName)"
                  class="w-4 h-4 rounded text-emerald-600 border-slate-300 focus:ring-emerald-500"
                />
                <div>
                  <div class="font-bold text-xs text-slate-900">{{ roleName }}</div>
                  <div class="text-[11px] text-slate-500">
                    @if (roleName === 'SuperAdmin') { Full system authority over institutions, services, and security logs }
                    @if (roleName === 'InstitutionAdmin') { Manage institution services, users, and DSR approvals }
                    @if (roleName === 'Reviewer') { Workflow stage reviewer for data exchange requests }
                    @if (roleName === 'Approver') { Final workflow stage approver for live connection keys }
                  </div>
                </div>
              </label>
            }
          </div>

          <div class="flex justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              (click)="showRolesModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 cursor-pointer"
            >
              Cancel
            </button>
            <button
              (click)="onSaveRoles()"
              class="px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-sm cursor-pointer"
            >
              Save Roles Matrix
            </button>
          </div>
        </div>
      </app-modal>

      <!-- Edit User Modal -->
      <app-modal
        [isOpen]="showEditModal()"
        [title]="'Edit Officer: ' + (userToEdit()?.firstName || '') + ' ' + (userToEdit()?.lastName || '')"
        [errorState]="modalError()"
        (close)="showEditModal.set(false)"
      >
        <form (ngSubmit)="onUpdateUser()" class="space-y-4">
          <div class="p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 flex justify-between items-center">
            <span>Email: <strong class="text-slate-900">{{ userToEdit()?.email }}</strong></span>
            <span class="font-mono text-[10px] bg-slate-200 text-slate-700 px-2 py-0.5 rounded font-bold">{{ userToEdit()?.rank }}</span>
          </div>

          <div class="grid grid-cols-3 gap-3">
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">First Name <span class="text-rose-500">*</span></label>
              <input
                type="text"
                [(ngModel)]="editUser.firstName"
                name="editFirstName"
                required
                placeholder="First Name"
                class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:outline-none"
              />
            </div>
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Middle Name</label>
              <input
                type="text"
                [(ngModel)]="editUser.middleName"
                name="editMiddleName"
                placeholder="Middle"
                class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:outline-none"
              />
            </div>
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Last Name <span class="text-rose-500">*</span></label>
              <input
                type="text"
                [(ngModel)]="editUser.lastName"
                name="editLastName"
                required
                placeholder="Last Name"
                class="w-full px-3 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:outline-none"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Phone Number <span class="text-rose-500">*</span></label>
              <app-phone-input
                [(ngModel)]="editUser.phone"
                name="editPhone"
                [required]="true"
                placeholder="777 000 000"
              ></app-phone-input>
            </div>

            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Account Status <span class="text-rose-500">*</span></label>
              <select
                [(ngModel)]="editUser.status"
                name="editStatus"
                required
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:outline-none"
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
                <option value="SUSPENDED">SUSPENDED</option>
              </select>
            </div>
          </div>

          <div class="flex justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              (click)="showEditModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-4 py-2 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 shadow-sm cursor-pointer"
            >
              Save Changes
            </button>
          </div>
        </form>
      </app-modal>

      <!-- Delete User Confirmation Modal -->
      <app-modal
        [isOpen]="showDeleteModal()"
        title="Confirm Officer Account Deletion"
        [errorState]="modalError()"
        (close)="showDeleteModal.set(false)"
      >
        <div class="space-y-4">
          <div class="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-800 flex items-start gap-2.5">
            <svg class="w-5 h-5 text-rose-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p class="font-bold">Are you sure you want to permanently delete this user officer?</p>
              <p class="mt-1">
                This will revoke system credentials and remove <strong>{{ userToDelete()?.firstName }} {{ userToDelete()?.lastName }}</strong> ({{ userToDelete()?.email }}) from {{ userToDelete()?.institutionName || 'the system' }}. This action cannot be undone.
              </p>
            </div>
          </div>

          <div class="flex justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              (click)="showDeleteModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              (click)="onConfirmDelete()"
              [disabled]="isDeleting()"
              class="px-4 py-2 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-500 shadow-sm cursor-pointer disabled:opacity-50"
            >
              {{ isDeleting() ? 'Deleting...' : 'Delete Officer' }}
            </button>
          </div>
        </div>
      </app-modal>
    </div>
  `
})
export class UserManagementComponent implements OnInit {
  userService = inject(UserService);
  toastService = inject(ToastService);
  authService = inject(AuthService);

  UserRank = UserRank;
  users = signal<UserProfileResponse[]>([]);
  searchQuery = '';

  showCreateModal = signal<boolean>(false);
  showRolesModal = signal<boolean>(false);
  showEditModal = signal<boolean>(false);
  showDeleteModal = signal<boolean>(false);
  isDeleting = signal<boolean>(false);

  userToEdit = signal<UserProfileResponse | null>(null);
  userToDelete = signal<UserProfileResponse | null>(null);

  editUser: UpdateUserRequest = {
    firstName: '',
    middleName: '',
    lastName: '',
    phone: '',
    status: 'ACTIVE'
  };

  newUser: CreateUserRequest = {
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    rank: UserRank.INSTITUTION_USER
  };

  selectedUser = signal<UserProfileResponse | null>(null);
  selectedRoles = signal<string[]>([]);
  modalError = signal<ModalErrorState | null>(null);

  // Institution Admin CANNOT give SuperAdmin role
  availableRoles = computed(() => {
    if (this.authService.hasRole('SuperAdmin') || this.authService.isInternal()) {
      return ['SuperAdmin', 'InstitutionAdmin', 'Reviewer', 'Approver'];
    }
    // Institution Admin scope: SuperAdmin excluded
    return ['InstitutionAdmin', 'Reviewer', 'Approver'];
  });

  filteredUsers = computed(() => {
    let list = this.users();
    if (this.searchQuery) {
      const q = this.searchQuery.toLowerCase();
      list = list.filter(u =>
        u.firstName.toLowerCase().includes(q) ||
        u.lastName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.institutionName && u.institutionName.toLowerCase().includes(q))
      );
    }
    return list;
  });

  ngOnInit() {
    this.loadUsers();
  }

  loadUsers() {
    this.userService.getAllUsers().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.users.set(res.data);
        }
      },
      error: () => {
        this.toastService.error('Failed to load user directory');
      }
    });
  }

  canEditUser(user: UserProfileResponse): boolean {
    if (this.authService.hasRole('SuperAdmin') || this.authService.isInternal()) {
      return true;
    }
    if (user.roles && (user.roles.includes('SuperAdmin') || user.rank === UserRank.INTERNAL)) {
      return false;
    }
    return this.authService.isInstitutionAdmin() || this.authService.hasPermission('can assign role');
  }

  canDeleteUser(user: UserProfileResponse): boolean {
    if (user.email === this.authService.currentUser()?.email) {
      return false; // Cannot delete self
    }
    if (this.authService.hasRole('SuperAdmin') || this.authService.isInternal()) {
      return true;
    }
    // Institution Admin cannot delete SuperAdmin or Internal users
    if (user.roles && (user.roles.includes('SuperAdmin') || user.rank === UserRank.INTERNAL)) {
      return false;
    }
    return this.authService.isInstitutionAdmin() || this.authService.hasPermission('can assign role');
  }

  canAssignRoles(user: UserProfileResponse): boolean {
    if (this.authService.hasRole('SuperAdmin') || this.authService.isInternal()) {
      return true;
    }
    // Institution Admin cannot assign roles to Super Admin users
    if (user.roles && (user.roles.includes('SuperAdmin') || user.rank === UserRank.INTERNAL)) {
      return false;
    }
    return this.authService.hasPermission('can assign role') || this.authService.isInstitutionAdmin();
  }

  openCreateModal() {
    this.modalError.set(null);
    this.newUser = {
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      rank: UserRank.INSTITUTION_USER
    };
    this.showCreateModal.set(true);
  }

  onCreateUser() {
    this.modalError.set(null);
    this.userService.createUser(this.newUser).subscribe({
      next: (res) => {
        if (res.success) {
          this.toastService.success(res.message);
          this.showCreateModal.set(false);
          this.loadUsers();
        } else if (res.success === false) {
          this.modalError.set(extractErrorDetails(res));
        }
      },
      error: (err) => {
        this.modalError.set(extractErrorDetails(err.error || err));
      }
    });
  }

  openAssignRolesModal(user: UserProfileResponse) {
    this.selectedUser.set(user);
    // Filter out SuperAdmin from pre-selection if not superadmin
    const userRoles = user.roles ? [...user.roles] : [];
    this.selectedRoles.set(userRoles);
    this.showRolesModal.set(true);
  }

  isRoleSelected(role: string): boolean {
    return this.selectedRoles().includes(role);
  }

  toggleRole(role: string) {
    if (this.isRoleSelected(role)) {
      this.selectedRoles.update(r => r.filter(x => x !== role));
    } else {
      this.selectedRoles.update(r => [...r, role]);
    }
  }

  onSaveRoles() {
    if (!this.selectedUser()) return;
    this.userService.assignRoles(this.selectedUser()!.uid, { roleNames: this.selectedRoles() }).subscribe({
      next: (res) => {
        if (res.success) {
          this.toastService.success(res.message);
          this.showRolesModal.set(false);
          this.loadUsers();
        }
      },
      error: (err) => {
        this.toastService.error(err.error?.message || 'Failed to update roles matrix');
      }
    });
  }

  openEditModal(user: UserProfileResponse) {
    this.modalError.set(null);
    this.userToEdit.set(user);
    this.editUser = {
      firstName: user.firstName,
      middleName: user.middleName || '',
      lastName: user.lastName,
      phone: user.phone,
      status: user.status || 'ACTIVE'
    };
    this.showEditModal.set(true);
  }

  onUpdateUser() {
    if (!this.userToEdit()) return;
    this.modalError.set(null);
    this.userService.updateUser(this.userToEdit()!.uid, this.editUser).subscribe({
      next: (res) => {
        if (res.success) {
          this.toastService.success(res.message || 'User updated successfully');
          this.showEditModal.set(false);
          this.loadUsers();
        } else if (res.success === false) {
          this.modalError.set(extractErrorDetails(res));
        }
      },
      error: (err) => {
        this.modalError.set(extractErrorDetails(err.error || err));
      }
    });
  }

  openDeleteModal(user: UserProfileResponse) {
    this.modalError.set(null);
    this.userToDelete.set(user);
    this.showDeleteModal.set(true);
  }

  onConfirmDelete() {
    if (!this.userToDelete()) return;
    this.modalError.set(null);
    this.isDeleting.set(true);
    this.userService.deleteUser(this.userToDelete()!.uid).subscribe({
      next: (res) => {
        this.isDeleting.set(false);
        if (res.success) {
          this.toastService.success(res.message || 'Officer deleted successfully');
          this.showDeleteModal.set(false);
          this.loadUsers();
        } else if (res.success === false) {
          this.modalError.set(extractErrorDetails(res));
        }
      },
      error: (err) => {
        this.isDeleting.set(false);
        this.modalError.set(extractErrorDetails(err.error || err));
      }
    });
  }
}
